import React, { memo, useEffect, useMemo } from 'react';
import { useAccount, useClub } from '~/features/club';
import { ButtonLegacy, RawHtml, Text } from '~/shared/components';
import { StyledMemberShip, StyledSelectedStore, StyledStoreName } from './styled';
import parse from 'html-react-parser';
import { useModule } from '~/templates';
import { M200ProfileModule } from '~/lib/data-contract';
import { useForm, useTranslation } from '~/shared/utils';
import { ToggleSwitch } from '~/shared/components/FormElements';
import { Spacer } from '~/theme';

export const MemberShip = memo(() => {
    const { account, updatePermissions, updatePermissionsIsLoading } = useAccount();
    const { translate } = useTranslation();
    const { toggleEditAccountStoreActive } = useClub();
    const { register, watch } = useForm({
        defaultValues: {
            email: account?.notifyByEmail,
            phone: account?.notifyBySms,
        },
    });
    const { element } = useModule<M200ProfileModule>();

    const { selectedStoreDescription, emailDescription, phoneDescription } = element;
    const [email, phone] = watch(['email', 'phone']);

    useEffect(() => {
        if (!account) {
            return;
        }

        if (email !== account.notifyByEmail || phone !== account.notifyBySms) {
            updatePermissions({
                receiveEmail: email,
                receiveSms: phone,
                storeId: account?.storeId,
                storeName: account?.storeName,
            });
        }
    }, [account, email, phone, updatePermissions]);

    const description = useMemo(() => parse(selectedStoreDescription || ''), [
        selectedStoreDescription,
    ]);

    return (
        <StyledMemberShip>
            <StyledSelectedStore>
                {account?.storeName && (
                    <StyledStoreName>
                        <Text
                            as="strong"
                            children={translate('club.profile.currentStoreDescription')}
                        />
                        {account?.storeName}
                    </StyledStoreName>
                )}
                <ButtonLegacy variant="secondary" onClick={() => toggleEditAccountStoreActive(true)}>
                    {translate('general.editInformation')}
                </ButtonLegacy>
                {selectedStoreDescription && (
                    <Text variant="caption" as="div">
                        {description}
                    </Text>
                )}
            </StyledSelectedStore>

            <Text as="strong" children={translate('form.label.email')} />
            <Spacer space="2" />
            <ToggleSwitch
                disabled={updatePermissionsIsLoading}
                {...register('email')}
                label={<RawHtml html={emailDescription} />}
            />
            <Spacer space="4" />
            <Text as="strong" children={translate('form.label.sms')} />
            <Spacer space="2" />
            <ToggleSwitch
                disabled={updatePermissionsIsLoading}
                {...register('phone')}
                label={<RawHtml html={phoneDescription} />}
            />
        </StyledMemberShip>
    );
});
